import React, { useState, useEffect, useCallback } from 'react';
import { Archive, RefreshCw, AlertTriangle, CheckCircle2, Loader2, Trash2, CheckCheck, Bell, Moon, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Maintenance
 *
 * Feature 1 — Archive Old GPS Points.
 *
 * Moves pre-TTL rows (docs missing ``created_at``) from
 * ``gps_tracking`` → ``gps_tracking_archive`` in 10 k-doc batches so the
 * new 30-day TTL sweep stays cheap forever. See v2.4.9 morning-burst RCA.
 */
export default function Maintenance() {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [running, setRunning] = useState(false);
  const [movedThisSession, setMovedThisSession] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [lastMessage, setLastMessage] = useState('');
  const [error, setError] = useState('');
  const abortRef = React.useRef(false);

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/maintenance/gps-archive-status`, {
        headers: { ...authHeader() },
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to fetch status');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runOneBatch = async () => {
    const r = await fetch(`${API_URL}/api/admin/maintenance/gps-archive-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ confirm: true, batch_size: 10000 }),
    });
    if (!r.ok) throw new Error(`Batch failed with ${r.status}`);
    return r.json();
  };

  const startArchive = async () => {
    if (running) return;
    if (!window.confirm(
      'This will move all pre-TTL GPS points to the archive collection in batches of 10,000.\n\n' +
      'Safe to run — batches are throttled so live tracking keeps working.\n\nProceed?'
    )) return;

    setRunning(true);
    setMovedThisSession(0);
    setBatchCount(0);
    setLastMessage('Starting…');
    setError('');
    abortRef.current = false;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (abortRef.current) {
          setLastMessage('Stopped by user.');
          break;
        }
        const res = await runOneBatch();
        setBatchCount(b => b + 1);
        if (!res.moved) {
          setLastMessage('All pre-TTL rows archived. TTL will keep the collection lean from here on.');
          break;
        }
        setMovedThisSession(m => m + res.moved);
        setLastMessage(`Archived ${res.moved.toLocaleString('en-IN')} rows in this batch…`);
        // Brief breather so we don't hog the DB during peak windows.
        await new Promise(r => setTimeout(r, 400));
      }
      await fetchStatus();
    } catch (e) {
      setError(e.message || 'Archive failed — check backend logs');
    } finally {
      setRunning(false);
    }
  };

  const stopArchive = () => {
    abortRef.current = true;
  };

  const preRemaining = status?.estimated_pre_ttl_remaining;
  const percentDone = (preRemaining != null && status?.estimated_total > 0)
    ? Math.max(0, Math.min(100, 100 * (1 - preRemaining / (preRemaining + (status?.archived_total || 0)))))
    : null;

  return (
    <div data-testid="admin-maintenance" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Maintenance</h1>
        <p className="text-sm text-gray-500 mt-1">
          One-off admin actions to keep the database lean and fast.
        </p>
      </div>

      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Archive size={20} className="text-indigo-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Archive Old GPS Points</h2>
            <p className="text-sm text-gray-600 mt-1">
              Moves pre-TTL rows (points captured before the 30-day auto-purge was
              wired) from <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">gps_tracking</code>
              {' '}into <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">gps_tracking_archive</code>.
              Runs in 10 000-row batches so live tracking stays responsive.
            </p>
          </div>
        </div>

        {/* Live stats */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total live rows" value={fmt(status?.estimated_total)} loading={loadingStatus} testid="stat-total" />
          <Stat label="Already indexed" value={fmt(status?.with_created_at)} loading={loadingStatus} testid="stat-indexed" hint="Has created_at (TTL-eligible)" />
          <Stat label="Pre-TTL remaining" value={fmt(preRemaining)} loading={loadingStatus} testid="stat-remaining" highlight={preRemaining > 0} />
          <Stat label="In archive" value={fmt(status?.archived_total)} loading={loadingStatus} testid="stat-archived" />
        </div>

        {/* Progress bar (only if we have both counts) */}
        {percentDone != null && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Archive progress</span>
              <span>{percentDone.toFixed(1)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 transition-all"
                style={{ width: `${percentDone}%` }}
                data-testid="archive-progress-bar"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={fetchStatus}
            disabled={loadingStatus || running}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="refresh-status-btn"
          >
            <RefreshCw size={14} className={loadingStatus ? 'animate-spin' : ''} />
            Refresh
          </button>
          {!running ? (
            <button
              onClick={startArchive}
              disabled={loadingStatus || preRemaining === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="start-archive-btn"
            >
              <Archive size={14} />
              {preRemaining === 0 ? 'Nothing to archive' : 'Start archiving'}
            </button>
          ) : (
            <button
              onClick={stopArchive}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
              data-testid="stop-archive-btn"
            >
              <Loader2 size={14} className="animate-spin" />
              Stop after current batch
            </button>
          )}
        </div>

        {/* Session log */}
        {(running || movedThisSession > 0 || lastMessage) && (
          <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm space-y-1" data-testid="archive-session-log">
            <div className="flex items-center gap-2 text-gray-700">
              {running ? (
                <><Loader2 size={14} className="animate-spin text-indigo-600" /> Running…</>
              ) : (
                <><CheckCircle2 size={14} className="text-emerald-600" /> Idle</>
              )}
              <span className="text-gray-500">Batches: <strong className="text-gray-900">{batchCount}</strong></span>
              <span className="text-gray-500">Moved this session: <strong className="text-gray-900">{movedThisSession.toLocaleString('en-IN')}</strong></span>
            </div>
            {lastMessage && <div className="text-xs text-gray-500">{lastMessage}</div>}
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="archive-error">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </section>

      <PurgeOldGpsSection />

      <PurgeNotificationsSection />

      <NightlyPurgeStatusCard />

      <BulkAutoApproveSection />
    </div>
  );
}

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN');
}

function Stat({ label, value, loading, testid, highlight, hint }) {
  return (
    <div
      className={`rounded-xl border p-3 ${highlight ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1">
        {loading ? <Loader2 size={16} className="animate-spin text-gray-400" /> : value}
      </div>
      {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}


/**
 * v2.5.0 — Purge GPS points older than N days (default 60).
 *
 * HARD DELETE (no archive copy). Safe because km summaries live on the
 * visit doc. Only the raw breadcrumb trail is removed — Live-Map replay
 * for stale visits will show "no trail" but the trip's km / expense /
 * status stay intact.
 */
function PurgeOldGpsSection() {
  const [olderThanDays, setOlderThanDays] = useState(45);
  const [running, setRunning] = useState(false);
  const [deletedThisSession, setDeletedThisSession] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [lastMessage, setLastMessage] = useState('');
  const [error, setError] = useState('');
  const abortRef = React.useRef(false);

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const runOne = async () => {
    const r = await fetch(`${API_URL}/api/admin/maintenance/gps-purge-old-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ confirm: true, older_than_days: olderThanDays, batch_size: 10000 }),
    });
    if (!r.ok) throw new Error(`Batch failed with ${r.status}`);
    return r.json();
  };

  const startPurge = async () => {
    if (running) return;
    if (!window.confirm(
      `PERMANENTLY DELETE all GPS breadcrumb points older than ${olderThanDays} days?\n\n` +
      'This is safe — visit km, expense, and status stay intact (they live on the visit doc). ' +
      'Only the raw ping trail disappears; Live-Map replay for very old trips will show "no trail".\n\n' +
      'Proceed?'
    )) return;

    setRunning(true);
    setDeletedThisSession(0);
    setBatchCount(0);
    setLastMessage('Starting…');
    setError('');
    abortRef.current = false;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (abortRef.current) { setLastMessage('Stopped by user.'); break; }
        const res = await runOne();
        setBatchCount(b => b + 1);
        if (!res.deleted) {
          setLastMessage(`Done. No more points older than ${olderThanDays} days.`);
          break;
        }
        setDeletedThisSession(d => d + res.deleted);
        setLastMessage(`Deleted ${res.deleted.toLocaleString('en-IN')} rows in this batch (cutoff ${res.cutoff.slice(0,10)})…`);
        await new Promise(r => setTimeout(r, 400));
      }
    } catch (e) {
      setError(e.message || 'Purge failed — check backend logs');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6" data-testid="purge-old-gps-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
          <Trash2 size={20} className="text-red-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Purge Old GPS Points</h2>
          <p className="text-sm text-gray-600 mt-1">
            Hard-delete raw GPS breadcrumbs older than the age you set below.
            <strong className="text-gray-800"> Km, expense, and visit status are NOT touched</strong> — they live on the visit doc.
            Use this to shrink <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">gps_tracking</code> when it's the biggest collection.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-end gap-4 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Delete points older than</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="30"
              max="3650"
              value={olderThanDays}
              onChange={e => setOlderThanDays(Math.max(30, Math.min(3650, Number(e.target.value) || 45)))}
              disabled={running}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
              data-testid="purge-days-input"
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-1">Minimum 30 days. Default 45.</div>
        </div>

        {!running ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={startPurge}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              data-testid="start-purge-btn"
            >
              <Trash2 size={14} />
              Start purging (this tab)
            </button>
            <BackgroundPurgeButton olderThanDays={olderThanDays} />
          </div>
        ) : (
          <button
            onClick={() => { abortRef.current = true; }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
            data-testid="stop-purge-btn"
          >
            <Loader2 size={14} className="animate-spin" />
            Stop after current batch
          </button>
        )}
      </div>

      <BackgroundPurgeStatusStrip />

      {(running || deletedThisSession > 0 || lastMessage) && (
        <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm space-y-1" data-testid="purge-session-log">
          <div className="flex items-center gap-2 text-gray-700 flex-wrap">
            {running ? (
              <><Loader2 size={14} className="animate-spin text-red-600" /> Running…</>
            ) : (
              <><CheckCircle2 size={14} className="text-emerald-600" /> Idle</>
            )}
            <span className="text-gray-500">Batches: <strong className="text-gray-900">{batchCount}</strong></span>
            <span className="text-gray-500">Deleted this session: <strong className="text-gray-900">{deletedThisSession.toLocaleString('en-IN')}</strong></span>
          </div>
          {lastMessage && <div className="text-xs text-gray-500">{lastMessage}</div>}
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="purge-error">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </section>
  );
}

/**
 * v2.5.0 — Bulk auto-approve the pending-approval backlog.
 *
 * One-time cleanup for the tens of thousands of visits and unplan
 * visits stuck in "pending" state after the approval workflow was
 * retired. Idempotent — safe to click again; second run reports zero.
 */
function BulkAutoApproveSection() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const run = async () => {
    if (busy) return;
    if (!window.confirm(
      'This will mark ALL currently-pending Visit Approvals, Unplan-Visit approvals, and Break approvals as auto-approved.\n\n' +
      'Safe: no visit / km / expense data is deleted or changed. Only the workflow status is flipped so the badges + backlog clear.\n\n' +
      'Runs in 5,000-row batches so it stays safe under load. Leave this tab open until "Done".\n\nProceed?'
    )) return;

    setBusy(true);
    setError('');
    setResult(null);
    const totals = { visits_updated: 0, non_plan_visits_updated: 0, breaks_updated: 0, batches: 0 };
    try {
      // v2.5.1 — loop batches until the server reports has_more=false.
      // Small breather between calls keeps Mongo responsive when a GPS
      // purge is running in parallel.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const r = await fetch(`${API_URL}/api/admin/maintenance/bulk-auto-approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ confirm: true }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        totals.batches += 1;
        totals.visits_updated += j.visits_updated || 0;
        totals.non_plan_visits_updated += j.non_plan_visits_updated || 0;
        totals.breaks_updated += j.breaks_updated || 0;
        setResult({ ...totals });
        if (!j.has_more) break;
        await new Promise(res => setTimeout(res, 300));
      }
    } catch (e) {
      setError(e.message || 'Bulk approve failed — check backend logs');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6" data-testid="bulk-approve-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <CheckCheck size={20} className="text-emerald-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Clear Pending-Approval Backlog</h2>
          <p className="text-sm text-gray-600 mt-1">
            One-time cleanup for the accumulated <strong>Visit Approvals</strong> and <strong>Unplan Visits</strong> pending queue.
            Since the approval workflow was retired in v2.5.0, this sweep flips every stuck row to <em>auto-approved</em>
            so the badges clear and reports render cleanly. Idempotent — safe to run twice.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            No visit, km, or expense data is deleted or changed.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <button
          onClick={run}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50"
          data-testid="bulk-approve-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
          {busy ? 'Running…' : 'Clear the backlog'}
        </button>
      </div>

      {result && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm" data-testid="bulk-approve-result">
          <div className="font-medium text-emerald-800 flex items-center gap-2">
            <CheckCircle2 size={16} /> Backlog cleared
          </div>
          <div className="text-xs text-emerald-700 mt-2 grid grid-cols-2 gap-2">
            <div>Visits: <strong>{result.visits_updated.toLocaleString('en-IN')}</strong></div>
            <div>Unplan visits: <strong>{result.non_plan_visits_updated.toLocaleString('en-IN')}</strong></div>
            <div>Breaks: <strong>{(result.breaks_updated || 0).toLocaleString('en-IN')}</strong></div>
            <div>Batches: <strong>{result.batches || 1}</strong></div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="bulk-approve-error">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </section>
  );
}


/**
 * v2.5.0 — Purge notifications (WhatsApp / SMS audit) older than N days.
 *
 * Notifications is a send-audit log. Old rows have no operational
 * value — dashboards and replay both scope to recent windows. Same
 * batched / stop-friendly UX as the GPS purge.
 */
function PurgeNotificationsSection() {
  const [olderThanDays, setOlderThanDays] = useState(90);
  const [running, setRunning] = useState(false);
  const [deletedThisSession, setDeletedThisSession] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [lastMessage, setLastMessage] = useState('');
  const [error, setError] = useState('');
  const abortRef = React.useRef(false);

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  const runOne = async () => {
    const r = await fetch(`${API_URL}/api/admin/maintenance/notifications-purge-old-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ confirm: true, older_than_days: olderThanDays, batch_size: 10000 }),
    });
    if (!r.ok) throw new Error(`Batch failed with ${r.status}`);
    return r.json();
  };

  const startPurge = async () => {
    if (running) return;
    if (!window.confirm(
      `PERMANENTLY DELETE all WhatsApp / SMS audit rows older than ${olderThanDays} days?\n\n` +
      'Safe — this is a send-audit log. Dashboards and replay only look at recent windows.\n\nProceed?'
    )) return;

    setRunning(true);
    setDeletedThisSession(0);
    setBatchCount(0);
    setLastMessage('Starting…');
    setError('');
    abortRef.current = false;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (abortRef.current) { setLastMessage('Stopped by user.'); break; }
        const res = await runOne();
        setBatchCount(b => b + 1);
        if (!res.deleted) {
          setLastMessage(`Done. No more notifications older than ${olderThanDays} days.`);
          break;
        }
        setDeletedThisSession(d => d + res.deleted);
        setLastMessage(`Deleted ${res.deleted.toLocaleString('en-IN')} rows in this batch…`);
        await new Promise(r => setTimeout(r, 400));
      }
    } catch (e) {
      setError(e.message || 'Purge failed — check backend logs');
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6" data-testid="purge-notifications-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center flex-shrink-0">
          <Bell size={20} className="text-pink-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Purge Old Notifications</h2>
          <p className="text-sm text-gray-600 mt-1">
            Hard-delete WhatsApp / SMS <strong>audit rows</strong> older than the age you set.
            The audit log grows unbounded (100k+ rows on prod) but only the last ~30 days are ever read by dashboards.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-end gap-4 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Delete rows older than</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min="30" max="3650"
              value={olderThanDays}
              onChange={e => setOlderThanDays(Math.max(30, Math.min(3650, Number(e.target.value) || 90)))}
              disabled={running}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 disabled:opacity-50"
              data-testid="notif-purge-days-input"
            />
            <span className="text-sm text-gray-600">days</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-1">Minimum 30 days. Default 90.</div>
        </div>

        {!running ? (
          <button
            onClick={startPurge}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 rounded-lg"
            data-testid="notif-start-purge-btn"
          >
            <Bell size={14} />
            Start purging
          </button>
        ) : (
          <button
            onClick={() => { abortRef.current = true; }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
            data-testid="notif-stop-purge-btn"
          >
            <Loader2 size={14} className="animate-spin" />
            Stop after current batch
          </button>
        )}
      </div>

      {(running || deletedThisSession > 0 || lastMessage) && (
        <div className="mt-5 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm space-y-1" data-testid="notif-purge-session-log">
          <div className="flex items-center gap-2 text-gray-700 flex-wrap">
            {running ? (
              <><Loader2 size={14} className="animate-spin text-pink-600" /> Running…</>
            ) : (
              <><CheckCircle2 size={14} className="text-emerald-600" /> Idle</>
            )}
            <span className="text-gray-500">Batches: <strong className="text-gray-900">{batchCount}</strong></span>
            <span className="text-gray-500">Deleted this session: <strong className="text-gray-900">{deletedThisSession.toLocaleString('en-IN')}</strong></span>
          </div>
          {lastMessage && <div className="text-xs text-gray-500">{lastMessage}</div>}
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2" data-testid="notif-purge-error">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </section>
  );
}


/**
 * v2.5.0 — read-only status card for the nightly auto-purge.
 *
 * Reassures admins that they don't need to click the manual buttons —
 * the scheduler runs both purges every night at 03:00 IST. Shows the
 * retention windows in effect + the fingerprint of the last successful
 * run (blank on a fresh redeploy — that's expected; the field
 * populates the first night after the scheduler ticks 03:00 IST).
 */
function NightlyPurgeStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeader = () => {
    const tok = localStorage.getItem('token');
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/maintenance/nightly-purge-status`, {
          headers: { ...authHeader() },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setStatus(d);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load status');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-6" data-testid="nightly-purge-status-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
          <Moon size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900">Nightly Auto-Purge</h2>
            {status?.enabled && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={10} /> Armed
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            The scheduler auto-purges old GPS points during off-peak hours only (<strong>22:00–06:00 IST</strong>) plus a full nightly sweep at 03:00 IST.
            No admin clicks needed — business-hour traffic is never impacted. Manual buttons above remain for on-demand cleanups.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-xs text-gray-500 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Loading status…
        </div>
      )}

      {status && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="Runs at (IST)" value={status.ist_time} testid="nightly-time" />
          <MiniStat label="GPS retention" value={`${status.gps_retention_days} days`} testid="nightly-gps-days" />
          <MiniStat label="Notifications retention" value={`${status.notifications_retention_days} days`} testid="nightly-notif-days" />
          <MiniStat label="Last run" value={status.last_run_date_ist || '—'} testid="nightly-last-run" hint={!status.last_run_date_ist ? 'Runs first at 03:00 IST' : null} />
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, testid, hint }) {
  return (
    <div className="bg-white/70 border border-indigo-100 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-indigo-500 font-medium">{label}</div>
      <div className="text-base font-bold text-gray-900 mt-1">{value}</div>
      {hint && <div className="text-[10px] text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}


/**
 * v2.5.2 — kick off a SERVER-SIDE background GPS purge.
 *
 * Unlike the browser-batched flow above, this fires off an
 * ``asyncio.create_task`` on the backend and returns immediately.
 * The admin can close the tab — the purge keeps running until the
 * collection is clean (or a pod restart / manual stop). Progress is
 * polled via ``BackgroundPurgeStatusStrip`` below.
 */
function BackgroundPurgeButton({ olderThanDays }) {
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (busy) return;
    if (!window.confirm(
      `Start a SERVER-SIDE background purge for GPS points older than ${olderThanDays} days?\n\n` +
      'This runs inside the backend — you can close this tab and it will keep going.\n' +
      'It uses 20,000-row batches so it\'s faster than the browser-tab version.\n\nProceed?'
    )) return;
    setBusy(true);
    try {
      const tok = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/maintenance/gps-purge-run-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
        body: JSON.stringify({ confirm: true, older_than_days: olderThanDays, batch_size: 20000 }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      window.dispatchEvent(new CustomEvent('bg-purge-started'));
    } catch (e) {
      alert(`Failed to start background purge: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
      data-testid="start-bg-purge-btn"
      title="Runs inside the backend — you can close this tab"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
      Run in background (much faster)
    </button>
  );
}

/**
 * Live status strip for the server-side purge. Polls every 3s and
 * shows an inline progress bar so you can see rows deleted in real
 * time. Auto-hides when there's no run history.
 */
function BackgroundPurgeStatusStrip() {
  const [status, setStatus] = useState(null);
  const [poll, setPoll] = useState(0);

  useEffect(() => {
    // Re-arm polling if a new run is started elsewhere on the page.
    const wake = () => setPoll(p => p + 1);
    window.addEventListener('bg-purge-started', wake);
    return () => window.removeEventListener('bg-purge-started', wake);
  }, []);

  useEffect(() => {
    let alive = true;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const tok = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/admin/maintenance/gps-purge-run-status`, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : {},
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!alive) return;
        setStatus(d);
        // Keep polling while running.
        if (d.running) timer = setTimeout(fetchOnce, 3000);
      } catch {
        if (alive) timer = setTimeout(fetchOnce, 5000);
      }
    };

    fetchOnce();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [poll]);

  if (!status || !status.job_id) return null;

  const stop = async () => {
    if (!window.confirm('Stop the background purge after the current batch?')) return;
    const tok = localStorage.getItem('token');
    await fetch(`${API_URL}/api/admin/maintenance/gps-purge-run-stop`, {
      method: 'POST',
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    });
    setPoll(p => p + 1);
  };

  const durationSec = (() => {
    if (!status.started_at) return 0;
    const start = new Date(status.started_at).getTime();
    const end = status.finished_at ? new Date(status.finished_at).getTime() : Date.now();
    return Math.max(0, (end - start) / 1000);
  })();
  const rate = durationSec > 0 ? Math.round(status.deleted / durationSec) : 0;

  return (
    <div
      className={`mt-4 rounded-xl border p-4 text-sm ${status.running ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
      data-testid="bg-purge-status-strip"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-gray-800 flex-wrap">
          {status.running ? (
            <><Loader2 size={14} className="animate-spin text-blue-600" /> <strong>Background purge running…</strong></>
          ) : status.stop_reason === 'user' ? (
            <><CheckCircle2 size={14} className="text-amber-600" /> <strong>Stopped by user</strong></>
          ) : (
            <><CheckCircle2 size={14} className="text-emerald-600" /> <strong>Background purge complete</strong></>
          )}
          <span className="text-gray-500 text-xs">
            <code className="bg-white px-1 py-0.5 rounded text-[10px]">{status.job_id}</code>
          </span>
        </div>
        {status.running && (
          <button
            onClick={stop}
            className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-medium"
            data-testid="stop-bg-purge-btn"
          >Stop</button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <MiniStat label="Batches" value={fmt(status.batches)} testid="bg-purge-batches" />
        <MiniStat label="Rows deleted" value={fmt(status.deleted)} testid="bg-purge-deleted" />
        <MiniStat label="Last batch" value={fmt(status.last_batch_deleted)} testid="bg-purge-last-batch" />
        <MiniStat label="Rate" value={rate > 0 ? `${fmt(rate)}/sec` : '—'} testid="bg-purge-rate" />
      </div>
      {status.last_error && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          Last error: {status.last_error}
        </div>
      )}
      <div className="text-[10px] text-gray-400 mt-2">
        You can safely close this tab — the purge keeps running on the server.
      </div>
    </div>
  );
}

